import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useState, type ReactNode } from 'react'
import { z } from 'zod'
import { createClient } from '@kairos/supabase/browser'

import {
  ErrorBanner,
  Field,
  Stack,
  SubmitButton,
} from '~/components/auth/auth-ui'
import { http } from '~/lib/http'
import { logger } from '~/lib/logger'

/**
 * `/authentication/change-password` — recovery sub-surface 5 from
 * `02-authentication.md` (forced password rotation).
 *
 * Used when an admin's `password_change_required` flag is set, e.g. after a
 * security event or first sign-in with a temporary password. Form fields:
 * current → new → confirm. After Supabase accepts the new password, we
 * call the admin-api `POST /api/auth/clear-password-change-flag` endpoint
 * to clear the flag, then route to `/admin`.
 *
 * The clear-flag endpoint may not exist yet on the admin-api side; if it
 * 404s we still let the user through (their password IS rotated and
 * Supabase tracks it) and log a warning so the missing endpoint is visible
 * in observability. This keeps the user from being soft-locked while the
 * admin-api work catches up.
 */

const formSchema = z
  .object({
    current: z.string().min(1, 'Enter your current password'),
    password: z
      .string()
      .min(12, 'Use at least 12 characters')
      .regex(/[A-Z]/, 'Add an uppercase letter')
      .regex(/\d/, 'Add a number')
      .regex(/[^A-Za-z0-9]/, 'Add a symbol'),
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  })
  .refine((data) => data.password !== data.current, {
    path: ['password'],
    message: 'Choose a password you have not used before',
  })

type FormValues = z.infer<typeof formSchema>

export const Route = createFileRoute('/authentication/change-password')({
  component: ChangePasswordPage,
})

function ChangePasswordPage(): ReactNode {
  const navigate = useNavigate()
  const [bannerError, setBannerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { current: '', password: '', confirm: '' },
  })

  async function onSubmit(values: FormValues): Promise<void> {
    setBannerError(null)
    try {
      const supabase = createClient()
      // CASA 2.4: send the current password so Supabase re-authenticates the
      // change instead of accepting a session that may have been hijacked.
      const { error } = await supabase.auth.updateUser({
        password: values.password,
        current_password: values.current,
      })
      if (error) {
        if (error.status === 401) {
          setBannerError('Your current password is incorrect.')
          return
        }
        setBannerError(error.message || 'We couldn&rsquo;t update your password. Try again.')
        return
      }

      // End other sessions (CASA 2.2.2).
      await supabase.auth.signOut({ scope: 'others' })

      // Clear the server-side password_change_required flag. If the endpoint
      // is not deployed yet (e.g. early Phase 2), we soft-fail so the user
      // is not locked into the change-password loop.
      try {
        await http.post('/api/auth/clear-password-change-flag')
      } catch (cause: unknown) {
        logger.warn('clear-password-change-flag failed (non-blocking)', {
          message: cause instanceof Error ? cause.message : 'unknown',
        })
      }

      void navigate({ to: '/admin' })
    } catch (cause: unknown) {
      logger.warn('change-password unexpected error', {
        message: cause instanceof Error ? cause.message : 'unknown',
      })
      setBannerError('Something went wrong. Try again.')
    }
  }

  return (
    <Stack>
      <header>
        <h1 className="ka-auth-h1">Change your password</h1>
        <p className="ka-auth-sub">Required by your security policy.</p>
      </header>

      {bannerError ? <ErrorBanner message={bannerError} /> : null}

      <form noValidate onSubmit={handleSubmit(onSubmit)} method="post" className="ka-stack">
        <Field
          id="current"
          label="Current password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          error={errors.current?.message}
          {...register('current')}
        />
        <Field
          id="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register('password')}
        />
        <Field
          id="confirm"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••"
          error={errors.confirm?.message}
          {...register('confirm')}
        />
        <SubmitButton loading={isSubmitting} label="Save new password" loadingLabel="Saving…" />
      </form>
    </Stack>
  )
}
